import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError } from '@services/apiClient'
import { sessionStore } from '@features/auth/services/sessionStore'
import { deleteVaultItemById, loadVaultItem, type VaultFlowDeps } from '../services/vaultFlow'
import { type VaultItem } from '../types'

/**
 * 個別 Vault アイテム取得 + 削除 (T-D-Vault-C、xlsx No.29)。
 *
 * state machine:
 *   - 'loading': mount 直後の GET 中
 *   - 'ready':   復号成功
 *   - 'error':   GET / 復号 失敗
 *   - 'confirm-delete': 削除確認ダイアログ表示中 (ready からの分岐)
 *   - 'deleting': DELETE 中 (二重実行防止 + UI 上のスピナー)
 *   - 'deleted':  削除成功 (caller が #vault へ navigate)
 *
 * 設計判断:
 *   - 削除は **必ず 2 段階確認** (ready → confirm-delete → deleting → deleted)。
 *     誤クリックで失われる UX 災害を防ぐ。dialog は <dialog> 要素ではなく
 *     state を見て描画 (テスト容易性 + jsdom 互換性)。
 *   - confirm 状態から cancel すれば ready (再 fetch せず) に戻る。
 *   - 復号失敗 (auth tag 不一致 / JSON 破損) は error 状態に。loadVaultItem は
 *     失敗を throw するので、ここで catch して error として保持する
 *     (1 件詳細画面では「壊れた」アイテムを描画するより明示的にエラー表示)。
 */

export type VaultDetailState =
  | { kind: 'loading' }
  | { kind: 'ready'; item: VaultItem }
  | { kind: 'confirm-delete'; item: VaultItem }
  | { kind: 'deleting'; item: VaultItem }
  | { kind: 'deleted' }
  | { kind: 'error'; messageKey: string }

const ERROR_KEYS = {
  no_session: 'vault.error.no_session',
  not_found: 'vault.detail.error.not_found',
  forbidden: 'vault.detail.error.forbidden',
  decrypt_failed: 'vault.detail.error.decrypt_failed',
  network: 'vault.error.network',
  generic: 'vault.error.generic',
} as const

const fetchMessageKeyFor = (err: unknown): string => {
  if (err instanceof ApiError) {
    if (err.status === 401) return ERROR_KEYS.no_session
    if (err.status === 404) return ERROR_KEYS.not_found
    if (err.status === 403) return ERROR_KEYS.forbidden

    return ERROR_KEYS.network
  }

  // 復号失敗 (AES-GCM OperationError / JSON SyntaxError / shape 不一致)
  if (err instanceof Error) {
    return ERROR_KEYS.decrypt_failed
  }

  return ERROR_KEYS.generic
}

const deleteMessageKeyFor = (err: unknown): string => {
  if (err instanceof ApiError) {
    if (err.status === 401) return ERROR_KEYS.no_session
    if (err.status === 404) return ERROR_KEYS.not_found
    if (err.status === 403) return ERROR_KEYS.forbidden

    return ERROR_KEYS.network
  }

  return ERROR_KEYS.generic
}

export interface UseVaultDetailOptions {
  /** テスト用 DI: vaultFlow の deps を上書きする。 */
  flowDeps?: VaultFlowDeps
}

export interface UseVaultDetail {
  state: VaultDetailState
  requestDelete: () => void
  cancelDelete: () => void
  confirmDelete: () => Promise<void>
  reload: () => Promise<void>
}

export const useVaultDetail = (id: string, options: UseVaultDetailOptions = {}): UseVaultDetail => {
  const [state, setState] = useState<VaultDetailState>({ kind: 'loading' })
  const mountedRef = useRef(true)
  // confirmDelete で「現在の state」を同期参照するための ref。
  // React 18 の setState updater は flush タイミングで呼ばれるため、
  // setState callback 内で `target = prev.item` を取り出して直後に検査する
  // パターンは flush 前だと target が null のまま進む race を起こす。
  const stateRef = useRef<VaultDetailState>(state)

  stateRef.current = state

  const fetchOnce = useCallback(async (): Promise<void> => {
    const session = sessionStore.getState()
    if (session.kind !== 'unlocked') {
      if (mountedRef.current) {
        setState({ kind: 'error', messageKey: ERROR_KEYS.no_session })
      }

      return
    }

    try {
      const item = await loadVaultItem(session.symmetricKey, id, options.flowDeps)
      if (!mountedRef.current) return
      setState({ kind: 'ready', item })
    } catch (err) {
      if (!mountedRef.current) return
      setState({ kind: 'error', messageKey: fetchMessageKeyFor(err) })
    }
  }, [id, options.flowDeps])

  useEffect(() => {
    mountedRef.current = true
    void fetchOnce()

    return () => {
      mountedRef.current = false
    }
  }, [fetchOnce])

  const requestDelete = (): void => {
    setState(prev => (prev.kind === 'ready' ? { kind: 'confirm-delete', item: prev.item } : prev))
  }

  const cancelDelete = (): void => {
    setState(prev => (prev.kind === 'confirm-delete' ? { kind: 'ready', item: prev.item } : prev))
  }

  const confirmDelete = async (): Promise<void> => {
    // 1 度しか呼ばれないようガード (UI 側でも disabled だが二重防止)。
    // stateRef は render 直後の最新 state を同期反映するため、setState callback
    // 内で取り出すよりも確実 (React 18 batching の影響を受けない)。
    const current = stateRef.current
    if (current.kind !== 'confirm-delete') return
    const target = current.item

    setState({ kind: 'deleting', item: target })

    try {
      await deleteVaultItemById(id, options.flowDeps)
      if (!mountedRef.current) return
      setState({ kind: 'deleted' })
    } catch (err) {
      if (!mountedRef.current) return
      setState({ kind: 'error', messageKey: deleteMessageKeyFor(err) })
    }
  }

  return {
    state,
    requestDelete,
    cancelDelete,
    confirmDelete,
    reload: fetchOnce,
  }
}
